import React from "react";

type BaseProps = {
  label?: string;
  containerClassName?: string;
  as?: "input" | "select" | "textarea";
  options?: { label: string; value: string }[]; // for dropdown
};

type InputProps = BaseProps & React.InputHTMLAttributes<HTMLInputElement>;

type SelectProps = BaseProps & React.SelectHTMLAttributes<HTMLSelectElement>;

type TextareaProps = BaseProps &
  React.TextareaHTMLAttributes<HTMLTextAreaElement>;

type Props = InputProps | SelectProps | TextareaProps;

const InputField = React.forwardRef<
  HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
  Props
>(
  (
    {
      label,
      containerClassName,
      className,
      id,
      as = "input",
      options,
      ...props
    },
    ref
  ) => {
    const baseStyles =
      "border-2 border-[#F1F1F1] px-4 md:px-6 py-3 md:py-4 text-[14px] md:text-[16px] lg:text-lg rounded-[12px] w-full text-[#0A0E2EBF] focus:outline-none focus:border-tomato-200";

    return (
      <div className={`w-full ${containerClassName}`}>
        {label && (
          <label
            htmlFor={id}
            className="text-[16px] md:text-[18px] lg:text-xl text-[#0A0E2EBF] font-medium block mb-2 md:mb-3"
          >
            {label}
          </label>
        )}

        {as === "select" ? (
          <select
            ref={ref as React.Ref<HTMLSelectElement>}
            id={id}
            className={`${baseStyles} ${className || ""}`}
            {...(props as React.SelectHTMLAttributes<HTMLSelectElement>)}
          >
            {options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : as === "textarea" ? (
          <textarea
            ref={ref as React.Ref<HTMLTextAreaElement>}
            id={id}
            className={`${baseStyles} ${className || ""} resize-none`}
            {...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
          />
        ) : (
          <input
            ref={ref as React.Ref<HTMLInputElement>}
            id={id}
            className={`${baseStyles} ${className || ""}`}
            {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
          />
        )}
      </div>
    );
  }
);

InputField.displayName = "InputField";

export default InputField;
